import { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { IMG, LOGO_URL } from './dionData';
import { Reveal, ThinLine } from './DionShared';

/* SCENE 01 — CINEMATIC HERO */
export const HeroScene = ({ onConsult, onNavigate }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.22]);
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="dion-top" ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden" data-testid="dion-hero">
      <motion.div className="absolute inset-0" style={reduced ? undefined : { scale, y }}>
        <img src={IMG.hero} alt="Symbolische Porträtaufnahme eines Mannes in architektonischem Interieur" className="dh-media-img" fetchPriority="high" />
      </motion.div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(10,13,20,.92) 0%, rgba(10,13,20,.55) 42%, rgba(10,13,20,.12) 75%), linear-gradient(0deg, rgba(10,13,20,.9), transparent 45%)' }} />

      <div className="relative mx-auto flex h-full max-w-[1600px] flex-col justify-between px-5 pb-8 pt-28 sm:px-8">
        <motion.div initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}>
          <span className="dh-eyebrow">Dion Hair Clinic — Germany</span>
        </motion.div>

        <motion.div className="max-w-3xl" style={reduced ? undefined : { opacity: fade }}>
          <Reveal delay={0.35}>
            <h1 className="dh-display text-[clamp(2.6rem,8vw,6rem)] text-white">
              IHR HAAR.<br /><span className="text-white/55">IHR AUSDRUCK.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.55}>
            <p className="mt-7 max-w-md text-base font-light leading-relaxed text-white/80 sm:text-lg">
              Haartransplantation mit ästhetischem Anspruch.
            </p>
          </Reveal>
          <Reveal delay={0.7} className="mt-6">
            <ThinLine width="72px" delay={0.9} />
            <p className="dh-kicker mt-4">Ästhetik × Medizin × Ingenieurtechnik</p>
          </Reveal>
          <Reveal delay={0.85} className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={onConsult} className="dh-btn dh-btn-solid" data-testid="hero-primary-cta">
              Beratung vereinbaren <ArrowUpRight size={16} />
            </button>
            <button onClick={() => onNavigate('methoden')} className="dh-btn dh-btn-ghost" data-testid="hero-secondary-cta">
              Methoden entdecken <ArrowDown size={16} />
            </button>
          </Reveal>
        </motion.div>

        <div className="flex items-end justify-between gap-6">
          <motion.img
            src={LOGO_URL} alt="Dion Hair Clinic" width="120" height="72"
            className="dh-logo h-9 w-auto opacity-0 sm:h-11"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 0.85, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1.1, duration: 1.2 }}
          />
          <motion.button
            onClick={() => onNavigate('detail')} aria-label="Weiter scrollen"
            className="grid h-11 w-11 place-items-center border border-white/20 text-white/80"
            animate={reduced ? undefined : { y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}
            data-testid="hero-scroll-cue"
          >
            <ArrowDown size={18} />
          </motion.button>
        </div>
      </div>
    </section>
  );
};

/* SCENE 02 — ENTER THE HAIR */
export const EnterHairScene = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const portraitOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const portraitScale = useTransform(scrollYProgress, [0, 0.5], [1.05, 1.25]);
  const follicleOpacity = useTransform(scrollYProgress, [0.32, 0.6], [0, 1]);
  const follicleScale = useTransform(scrollYProgress, [0.3, 1], [1.3, 1.02]);
  const t1 = useTransform(scrollYProgress, [0.08, 0.2, 0.42, 0.5], [0, 1, 1, 0]);
  const t2 = useTransform(scrollYProgress, [0.55, 0.7], [0, 1]);

  return (
    <section id="detail" ref={ref} className="relative h-[260vh]" data-testid="dion-enter-hair">
      <div className="sticky top-0 h-[100svh] overflow-hidden bg-[#0a0d14]">
        <motion.div className="absolute inset-0" style={reduced ? undefined : { opacity: portraitOpacity, scale: portraitScale }}>
          <img src={IMG.portrait} alt="Symbolisches Porträt, Fokus auf die Haarlinie" className="dh-media-img" />
        </motion.div>
        <motion.div className="absolute inset-0" style={reduced ? { opacity: 1 } : { opacity: follicleOpacity, scale: follicleScale }} data-testid="scene-02-3d-trigger">
          <img src={IMG.follicle} alt="Elegante Makroaufnahme einzelner Haarfollikel — Symbolbild" className="dh-media-img" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14] via-transparent to-[#0a0d14]/40" />

        <div className="absolute inset-0 mx-auto flex max-w-[1600px] items-center px-5 sm:px-8">
          <div className="max-w-xl">
            <motion.h2 className="dh-display text-[clamp(2rem,5.5vw,4rem)] text-white" style={reduced ? undefined : { opacity: t1 }}>
              WAS NATÜRLICH WIRKT,
            </motion.h2>
            <motion.h2 className="dh-display dh-display-strong text-[clamp(2rem,5.5vw,4rem)] text-[#5b8da8]" style={reduced ? undefined : { opacity: t2 }}>
              BEGINNT IM DETAIL.
            </motion.h2>
            <motion.p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70" style={reduced ? undefined : { opacity: t2 }}>
              Jede Follikeleinheit wird im natürlichen Wuchswinkel geplant. Jedes Ergebnis beginnt mit Präzision.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* SCENE 03 — HAIRLINE DESIGN */
export const HairlineScene = () => {
  const reduced = useReducedMotion();
  const draw = {
    hidden: { pathLength: 0, opacity: 0 },
    show: (i) => ({ pathLength: 1, opacity: 0.7, transition: { pathLength: { duration: 1.6, delay: 0.2 + i * 0.25, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.4, delay: 0.2 + i * 0.25 } } }),
  };
  return (
    <section id="hairline" className="relative overflow-hidden bg-[#0a0d14] py-24 lg:py-36" data-testid="dion-hairline">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <div className="relative order-2 aspect-[4/5] overflow-hidden lg:order-1" data-testid="hairline-canvas-container">
          <img src={IMG.portrait} alt="Symbolisches Dreiviertel-Porträt für die Haarlinienplanung" className="dh-media-img" />
          <svg viewBox="0 0 400 500" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <motion.path d="M70 150 C 140 96, 250 90, 330 132" fill="none" stroke="#5b8da8" strokeWidth="1.2"
              variants={reduced ? undefined : draw} custom={0} initial={reduced ? undefined : 'hidden'} whileInView="show" viewport={{ once: true, amount: 0.5 }} />
            <motion.path d="M84 176 C 150 128, 250 124, 322 164" fill="none" stroke="#5b8da8" strokeWidth="0.8" strokeDasharray="3 5"
              variants={reduced ? undefined : draw} custom={1} initial={reduced ? undefined : 'hidden'} whileInView="show" viewport={{ once: true, amount: 0.5 }} />
            <motion.path d="M120 96 L 120 150 M 200 88 L 200 150 M 280 100 L 280 150" fill="none" stroke="#5b8da8" strokeWidth="0.6"
              variants={reduced ? undefined : draw} custom={2} initial={reduced ? undefined : 'hidden'} whileInView="show" viewport={{ once: true, amount: 0.5 }} />
          </svg>
        </div>
        <div className="order-1 lg:order-2">
          <Reveal>
            <span className="dh-eyebrow">03 — Haarlinien-Design</span>
            <h2 className="dh-display mt-6 text-[clamp(2rem,5vw,3.6rem)] text-white">
              KEINE STANDARDLINIE.<br /><span className="text-white/55">IHR GESICHT.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-7">
            <ThinLine width="60px" />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
              Die Planung der Haarlinie berücksichtigt Gesichtsproportionen, Alter, vorhandenes Haar und die
              langfristige ästhetische Entwicklung. Eine bewusst unregelmäßige, natürliche Linie – keine Schablone.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
